import React from "react";
import NavBar from "component/NavBar";
import Footer from "component/Footer";
import Phone from "component/Phone";
import { moodswing } from "assets";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faCheckCircle,
  faComments,
  faPrescriptionBottleMedical,
} from "@fortawesome/free-solid-svg-icons";

const depressionSymptoms = [
  "Persistent sadness or emptiness",
  "Loss of interest in activities",
  "Changes in appetite or weight",
  "Sleep disturbances",
  "Fatigue or low energy",
  "Feelings of worthlessness or guilt",
];

const treatmentApproach = [
  {
    icon: faHeart,
    title: "Comprehensive Evaluation",
    description: "Understanding the root causes of your depression",
  },
  {
    icon: faPrescriptionBottleMedical,
    title: "Medication Management",
    description: "Personalized pharmaceutical interventions when appropriate",
  },
  {
    icon: faComments,
    title: "Therapeutic Support",
    description: "Building effective strategies for managing symptoms",
  },
];

const Depression = () => {
  return (
    <div className="bg-white">
      <NavBar />
      <Phone />

      <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
        <img
          className="w-full h-full object-cover"
          src={moodswing}
          alt="Depression Treatment"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center animate-fade-in">
            <div className="inline-block px-4 py-2 bg-primary-500/90 rounded-full mb-4">
              <span className="text-white font-semibold text-body-sm">Mental Health Service</span>
            </div>
            <h1 className="text-display-sm md:text-display-md lg:text-display-lg text-white font-bold mb-4">
              Depression Treatment
            </h1>
            <p className="text-body-lg md:text-heading-sm text-white/90 max-w-2xl mx-auto">
              Compassionate care for understanding and overcoming depression
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-6">
              Understanding Depression
            </h2>
            <div className="h-1 w-24 bg-primary-500 mx-auto mb-8"></div>
          </div>

          <div className="bg-white rounded-xl shadow-medium p-8 md:p-10">
            <p className="text-body-lg text-neutral-700 leading-relaxed mb-6">
              Depression is a complex behavioral health disorder that manifests through diverse symptoms beyond just sadness or loneliness. While these emotions may be present, they don't fully capture the complexity of depression. Many individuals outwardly appear content while quietly struggling with depression.
            </p>
            <p className="text-body-lg text-neutral-700 leading-relaxed">
              If you're experiencing feelings of despair, hopelessness, or profound grief, we're here to help. Our services are carefully tailored to your specific needs, focusing on identifying the root causes of your depression and developing effective strategies to manage it constructively. You don't have to face this alone.
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Recognizing Depression Symptoms
            </h2>
            <p className="text-body-lg text-neutral-600">
              Depression affects everyone differently, but common signs include
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {depressionSymptoms.map((symptom, index) => (
              <div
                key={index}
                className="bg-neutral-50 rounded-xl p-6 hover:bg-primary-50 transition-colors duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-primary-500 text-xl group-hover:text-primary-600 transition-colors"
                    />
                  </div>
                  <p className="text-body-md text-neutral-800 font-medium">
                    {symptom}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Our Approach to Depression Treatment
            </h2>
            <p className="text-body-lg text-neutral-600 max-w-2xl mx-auto">
              Personalized, evidence-based care focused on your recovery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {treatmentApproach.map((approach, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 p-8 text-center group"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-500 transition-colors">
                  <FontAwesomeIcon
                    icon={approach.icon}
                    className="text-2xl text-primary-600 group-hover:text-white transition-colors"
                  />
                </div>
                <h3 className="text-heading-sm text-neutral-900 mb-3">
                  {approach.title}
                </h3>
                <p className="text-body-md text-neutral-600 leading-relaxed">
                  {approach.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary-500 to-primary-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-heading-xl md:text-display-sm text-white mb-6">
            Begin Your Journey to Recovery
          </h2>
          <p className="text-body-lg text-white/90 mb-8 leading-relaxed">
            Depression is treatable. With the right support and treatment plan, you can regain hope and joy in your life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-neutral-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                Start Your Recovery
              </button>
            </Link>
            <Link to="/mainservicepage">
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300">
                Learn More
              </button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Depression;
